/* eslint-disable ember/no-side-effects */
/* eslint-disable ember/no-runloop */
import { set } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import { PRISM_SPACING_500 } from '@mylighthouse/prism-foundation';
import dayjs from 'dayjs';
import { task, timeout } from 'ember-concurrency';
import { COLOR_DATA_PARITY_BENCHMARK } from 'frontend-utils/utils/css-variable';
import cloneDeep from 'lodash.clonedeep';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import ParityTooltipsTooltipKpi from 'lumen/components/parity/tooltips/tooltip-kpi';
import { TOOLTIP_HOVER_TIMEOUT } from 'lumen/services/usage-analytics';
import { xValue } from 'lumen/utils/charts/chart-utils';
import {
  PARITY_COLORS,
  PREDICTED_PARITY_COLORS,
} from 'lumen/utils/constants/chart-color-constants';
import {
  HIGHCHARTS_MISSING_MARKER,
  HIGHCHARTS_SELECTED_MARKER,
} from 'lumen/utils/constants/general-constants';
import {
  BENCHMARK,
  DATE_AGGREGATION_MONTH,
  DATE_AGGREGATION_WEEK,
  LATEST,
  PARITY_FILTER,
} from 'lumen/utils/constants/pi-constant-values';
import {
  calculateBenchmarkPercentages,
  calculateParityAnalyticsValues,
} from 'lumen/utils/parity-analytics/calculate-parity-analytics-values';
import {
  getFormattedLastPossibleExtractDate,
  parityAnalyticsDateFormat,
} from 'lumen/utils/parity-analytics/date-utils';
import roundParityPercentages from 'lumen/utils/parity-analytics/round-parity-percentages';

interface ParityChartsChartParityEvolutionSignature {
  Args: {
    customLegendItemClick: unknown;
    data: unknown;
    dateAggregation: unknown;
    extractDate: unknown;
    hideHotelsMetric: unknown;
    isBenchmarkVisible: unknown;
    isLoading: unknown;
    parityFilters: unknown;
    periodChanged: unknown;
  };
}

/**
 * Required
 * @param { Object } data The historical data to show in the chart
 * @param { String } dateAggregation week or month
 * @param { Boolean } isLoading Whether the data is still loading or not
 * Optional
 * @param { String } extractDate extractDate QP: marks the end of the selected period (week or month)
 * @param { String } historicalExtractDate historicalExtractdate QP: marks the end of the chart
 * @param { function } periodChanged Method called when clicking a point in the graph
 * @param { Set<String> } parityFilters Set of enabled filters (win, meet, loss)
 * @param { function } customLegendItemClick Method called when interacting with the chart legend
 */
export default class ParityChartsChartParityEvolution extends Component<ParityChartsChartParityEvolutionSignature> {
  // eslint-disable-next-line ember/no-unused-services
  @service declare dates: Services['dates'];
  // eslint-disable-next-line ember/no-unused-services
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  trackHoverTask = task({ restartable: true }, async (formattedDate) => {
    await timeout(TOOLTIP_HOVER_TIMEOUT);
    this.usageAnalytics.track('Evolution Details Viewed', {
      eventTrigger: 'evolution-chart',
      periodType: this.args.dateAggregation,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      shopDate: formattedDate,
    });
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    if (this.args.isLoading) {
      return [];
    }

    const chartData = [];
    const [loss, meet, win] = this.splitWinMeetLoss();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const isWinVisible = this.args.parityFilters.has(PARITY_FILTER.WIN);
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const isMeetVisible = this.args.parityFilters.has(PARITY_FILTER.MEET);
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const isLossVisible = this.args.parityFilters.has(PARITY_FILTER.LOSS);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const allFiltersEnabled = isLossVisible && isMeetVisible && isWinVisible;
    const allFiltersDisabled =
      !isLossVisible && !isMeetVisible && !isWinVisible;

    chartData.push({
      id: PARITY_FILTER.WIN,
      name: this.intl.t('parity_analytics.win').toString(),
      // @ts-expect-error: Incorrect type
      data: win.data,
      zoneAxis: 'x',
      // @ts-expect-error: Incorrect type
      zones: win.zones,
      type: 'area',
      fillColor: PARITY_COLORS[PARITY_FILTER.WIN].fillColor,
      color: PARITY_COLORS[PARITY_FILTER.WIN].color,
      zIndex: 10, // Required (otherwise areas don't overlap nicely)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      visible: isWinVisible,
      legendIndex: 3,
    });

    chartData.push({
      id: PARITY_FILTER.MEET,
      name: this.intl.t('parity_analytics.meet').toString(),
      // @ts-expect-error: Incorrect type
      data: meet.data,
      zoneAxis: 'x',
      // @ts-expect-error: Incorrect type
      zones: meet.zones,
      type: 'area',
      fillColor: PARITY_COLORS[PARITY_FILTER.MEET].fillColor,
      color: PARITY_COLORS[PARITY_FILTER.MEET].color,
      zIndex: 10,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      visible: isMeetVisible,
      legendIndex: 2,
    });

    chartData.push({
      id: PARITY_FILTER.LOSS,
      name: this.intl.t('parity_analytics.loss').toString(),
      // @ts-expect-error: Incorrect type
      data: loss.data,
      zoneAxis: 'x',
      // @ts-expect-error: Incorrect type
      zones: loss.zones,
      type: 'area',
      fillColor: PARITY_COLORS[PARITY_FILTER.LOSS].fillColor,
      color: PARITY_COLORS[PARITY_FILTER.LOSS].color,
      zIndex: 10,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      visible: isLossVisible,
      legendIndex: 1,
    });

    const benchmark = this.calculateBenchmark();
    chartData.push({
      id: BENCHMARK,
      name: this.intl.t('demand.benchmark').toString(),
      type: 'line',
      data: benchmark.data,
      zones: benchmark.zones,
      zoneAxis: 'x',
      color: `var(${COLOR_DATA_PARITY_BENCHMARK})`,
      zIndex: 11,
      visible:
        this.args.isBenchmarkVisible &&
        !(allFiltersEnabled || allFiltersDisabled),
      legendIndex: 4,
      marker: {
        enabled: false,
        states: {
          hover: { enabled: false },
          select: { enabled: false },
        },
      },
    });

    return chartData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return {
      chart: {
        type: 'area',
        height: '346px',
        spacingTop: PRISM_SPACING_500,
        spacingBottom: 0,
        spacingLeft: PRISM_SPACING_500,
        spacingRight: PRISM_SPACING_500,
        zoomType: null,
      },
      title: {
        text: 'parity-evolution-chart',
      },
      tooltip: {
        positioner: null,
        shared: true,
        hideDelay: 0.2,
        shape: 'square',
        padding: 0,
        borderWidth: 0,
        shadow: false,
        outside: true,
        distance: 40,

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // Necessary to get correct data for tooltips, needs to be in separate runloop
          begin();

          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const numberOfPoints = this.points.length;

          if (numberOfPoints) {
            // Set the tooltip content
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            const { point } = this.points[0];
            // Copy over values for tooltip
            const barValues = {
              win: {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                value: point.history.win,
              },
              meet: {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                value: point.history.meet,
              },
              loss: {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                value: point.history.loss,
              },
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              totalNbOfShops: point.history.totalNbOfShops,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              totalNbOfHotels: point.history.subscriptions,
            };

            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const benchmarkPoint = this.points.find(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              (p) => p.point.type === BENCHMARK,
            );
            if (benchmarkPoint && that.args.isBenchmarkVisible) {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              barValues.benchmark = benchmarkPoint.y;
            }

            set(that, 'barValues', barValues);

            // Set the tooltip title
            if (that.args.dateAggregation === DATE_AGGREGATION_WEEK) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              const isCurrentPointLatest = point.isLatest;
              if (isCurrentPointLatest) {
                set(
                  that,
                  'displayName',
                  that.intl.t('benchmark.current_score').toString(),
                );
              } else {
                const formattedExtractDate = dayjs
                  // @ts-expect-error: Incorrect type
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  .unix(this.x)
                  .utc()
                  .format('ll');
                const formattedExtractDateEndOfWeek = dayjs
                  // @ts-expect-error: Incorrect type
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  .unix(this.x)
                  .add(6, 'day')
                  .utc()
                  .format('ll');
                set(
                  that,
                  'displayName',
                  `${formattedExtractDate} - ${formattedExtractDateEndOfWeek}`,
                );
              }
            } else if (that.args.dateAggregation === DATE_AGGREGATION_MONTH) {
              set(
                that,
                'displayName',
                dayjs
                  // @ts-expect-error: Incorrect type
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  .unix(this.x)
                  .utc()
                  .format(that.dates.partialDateFormat.YEAR_AND_MONTH_LONG),
              );
            }
          }
          end();
          // @ts-expect-error: Incorrect type
          return document.getElementById('dummy-tooltip-for-history').innerHTML;
        },
        useHTML: true,
      },
      xAxis: {
        allowDecimals: false,
        type: 'datetime',
        endOnTick: true,
        ordinal: false,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        tickPositioner() {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (this.series[0]) {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const ticks = this.series[0].processedXData.slice();
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            ticks.info = this.tickPositions.info;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return ticks;
          }
          return null;
        },
        labels: {
          rotation: -45,
          align: 'right',
          x: 10,
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            // @ts-expect-error: Incorrect type
            if (this.value === xValue(LATEST)) {
              return that.intl.t('general.today').toString();
            }

            // Show the last day of the week
            if (that.args.dateAggregation === DATE_AGGREGATION_WEEK) {
              return parityAnalyticsDateFormat(
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                dayjs.unix(this.value).add(6, 'day').utc(),
                that.args.dateAggregation,
              );
            }

            return parityAnalyticsDateFormat(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              dayjs.unix(this.value).utc(),
              // @ts-expect-error: Incorrect type
              that.args.dateAggregation,
            );
          },
        },
      },
      yAxis: [
        {
          endOnTick: true,
          ordinal: false,
          showFirstLabel: false,
          min: 0,
          max: 1,
          labels: {
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            formatter() {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              return that.intl.formatNumber(this.value, { style: 'percent' });
            },
          },
          title: {
            text: '',
          },
        },
        {
          opposite: true,
          showFirstLabel: false,
          min: 0,
          title: {
            text: '',
          },
        },
      ],
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        enabled: true,
        borderWidth: 0,
        symbolWidth: 7,
        symbolHeight: 7,
        itemStyle: {
          width: 200,
          fontSize: '12px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
      plotOptions: {
        area: {
          stacking: true,
          fillOpacity: 1,
          opacity: 1,
          point: {
            events: {
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              mouseOver() {
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                that.trackHoverTask.perform(this.formattedDate);

                // @ts-expect-error: Incorrect type
                if (this.isSelectable) {
                  // @ts-expect-error: Incorrect type
                  // We use parentGroup because otherwise the cursor only changes when hovering on the inside
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  this.graphic.parentGroup.element.style.cursor = 'pointer';
                } else {
                  // @ts-expect-error: Incorrect type
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  this.graphic.parentGroup.element.style.cursor = 'auto';
                }
              },
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              mouseOut() {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                that.trackHoverTask.cancelAll();
              },
            },
          },
        },
        series: {
          events: {
            // Intercept legend actions (hide/show series)
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            legendItemClick(event) {
              if (that.args.customLegendItemClick) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                event.preventDefault();
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                that.args.customLegendItemClick(this.options.id);
              }
            },
          },
          point: {
            events: {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              click(e) {
                // Timestamp * 1000 for highcharts x-axis conversion to real unix timestamp
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                const period = dayjs.utc(e.point.x * 1000);

                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if (e.point.isSelectable) {
                  that.usageAnalytics.track('Date Changed', {
                    eventTrigger: 'evolution-chart',
                    periodType: that.args.dateAggregation,
                    startDaysOffsetFromToday: period.diff(
                      that.hotelAndMarketService.getLocalTime(),
                      'days',
                    ),
                  });

                  // @ts-expect-error: Incorrect type
                  that.args.periodChanged(period);
                }
              },
            },
          },
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLatestSelected() {
    return (
      this.args.dateAggregation === DATE_AGGREGATION_WEEK &&
      // @ts-expect-error: Incorrect type
      dayjs(this.args.extractDate).isAfter(
        getFormattedLastPossibleExtractDate(),
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedKeys() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return Object.keys(this.args.data?.all).sort(
      (a, b) => xValue(a) - xValue(b),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  calculateBenchmark() {
    // @ts-expect-error: Incorrect type
    const zones = [];
    // @ts-expect-error: Incorrect type
    const data = [];

    let historicalDataIndex = 0;
    this.sortedKeys.forEach((date) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const point = this.args.data?.all[date][0];

      let benchmarkValues;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (point && point.benchmark) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        benchmarkValues = point.benchmark;
      }

      const x = xValue(date);
      const dataPoint = roundParityPercentages(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        calculateBenchmarkPercentages(benchmarkValues),
      );
      const noShops = dataPoint.totalNbOfShops === 0;
      if (noShops) {
        data[historicalDataIndex] = {
          type: BENCHMARK,
          x,
          y: null,
        };
      } else {
        const graphPoint = cloneDeep(dataPoint);

        if (date === LATEST) {
          this.markZoneAsDashed(graphPoint);
        }

        // @ts-expect-error: Incorrect type
        if (graphPoint.dashed) {
          zones.push({
            value: x,
            dashStyle: 'ShortDot',
          });
        } else {
          zones.push({
            value: x,
            dashStyle: 'solid',
          });
        }

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.parityFilters.forEach((key) => {
          // @ts-expect-error: Incorrect type
          if (!data[historicalDataIndex]) {
            data[historicalDataIndex] = {
              type: BENCHMARK,
              x,
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              y: graphPoint[key],
              isSelectable: true,
            };
          } else {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            data[historicalDataIndex].y += graphPoint[key];
          }
        });
      }
      historicalDataIndex += 1;
    });

    // @ts-expect-error: Incorrect type
    return { data, zones };
  }

  // @ts-expect-error: Incorrect type
  markPointAsSelected(point): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    point.isSelected = true;
  }

  // @ts-expect-error: Incorrect type
  markPointWithNoData(currentPoint, previousPoint): void {
    // Pad values with previous values (no pits in graph)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    Object.keys(currentPoint)
      .filter((key) => key !== 'totalNbOfShops')
      .forEach((key) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (previousPoint && previousPoint[key]) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          currentPoint[key] = previousPoint[key];
        }
      });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    currentPoint.crossed = true;
  }

  // @ts-expect-error: Incorrect type
  markZoneAsDashed(point): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    point.dashed = true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  splitWinMeetLoss() {
    const previousPoint = {};
    const winMeetLoss = {
      loss: { data: [], zones: [] },
      meet: { data: [], zones: [] },
      win: { data: [], zones: [] },
    };

    this.sortedKeys.forEach((date) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const point = this.args.data?.all[date][0];
      let pointValues;

      if (point) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        pointValues = point.value;
      }

      const dataPoint = roundParityPercentages(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        calculateParityAnalyticsValues(pointValues),
      );

      // Add subscriptions count to the tooltip history point
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (pointValues && pointValues.subscriptions) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        dataPoint.subscriptions = pointValues.subscriptions;
      }

      // We make a copy, because we mutate the graph point when there is a no-data point present
      const graphPoint = cloneDeep(dataPoint);

      const x = xValue(date);
      const isLatest = date === LATEST;
      if (isLatest) {
        this.markZoneAsDashed(graphPoint);
        // Mark Latest as selected when the extractDate (=today) is after the getFormattedLastPossibleExtractDate (=second last Monday)
        if (this.isLatestSelected) {
          this.markPointAsSelected(graphPoint);
        }
      }

      // If the selected extractDate (YYYY-MM-DD for weekly, YYYY-MM for monthly) is equal to this graph point, we want to mark it as selected
      else if (
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.extractDate?.indexOf(date) > -1 &&
        !this.isLatestSelected
      ) {
        this.markPointAsSelected(graphPoint);
      }

      // Check if current dataPoint has number of shops
      if (graphPoint.totalNbOfShops === 0) {
        // @ts-expect-error: Incorrect type
        this.markPointWithNoData(graphPoint, previousPoint.data);
        this.markZoneAsDashed(graphPoint);
      }

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (previousPoint.data && previousPoint.data.totalNbOfShops === 0) {
        this.markZoneAsDashed(graphPoint);
      }

      Object.keys(winMeetLoss).forEach((key) => {
        // Set up an undefined marker for this point to use
        let marker;

        // Set zones when current point has no shops or previous had no shops
        // @ts-expect-error: Incorrect type
        if (graphPoint.dashed) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          winMeetLoss[key].zones.push({
            value: x,
            dashStyle: 'dash',
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            fillColor: PREDICTED_PARITY_COLORS[key].fillColor,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            color: PREDICTED_PARITY_COLORS[key].color,
          });
        } else {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          winMeetLoss[key].zones.push({
            value: x,
            dashStyle: 'solid',
          });
        }

        // @ts-expect-error: Incorrect type
        if (graphPoint.isSelected) {
          marker = HIGHCHARTS_SELECTED_MARKER;
        }

        // @ts-expect-error: Incorrect type
        if (graphPoint.crossed) {
          marker = HIGHCHARTS_MISSING_MARKER;
        }

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        winMeetLoss[key].data.push({
          type: 'parity',
          x,
          formattedDate: date,
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          y: graphPoint[key],
          history: dataPoint,
          // Default marker for crossed points! They can be (de)selected, and ota-chart needs to know the default!
          // @ts-expect-error: Incorrect type
          defaultMarker: graphPoint.crossed ? marker : undefined,
          marker,
          // We don't want the user to select crossed datapoints (NOTE: Datepicker can however select them!)
          // @ts-expect-error: Incorrect type
          isSelectable: !graphPoint.crossed,
          isLatest,
        });
      });

      // Remember point for padding in future potential missing data
      // @ts-expect-error: Incorrect type
      previousPoint.x = x;
      // @ts-expect-error: Incorrect type
      previousPoint.data = graphPoint;
    });

    return [winMeetLoss.loss, winMeetLoss.meet, winMeetLoss.win];
  }

  <template>
    <PrismCard @contentLayout="noPadding">
      <:content>
        <GeneralChartsOtaChart
          {{! @glint-expect-error: Incorrect type }}
          @chartOptions={{this.chartOptions}}
          @model={{this.chartData}}
        />
      </:content>
    </PrismCard>

    <div class="parity-tooltip hidden" id="dummy-tooltip-for-history">
      <ParityTooltipsTooltipKpi
        {{! @glint-expect-error: Incorrect type }}
        @data={{this.barValues}}
        {{! @glint-expect-error: Incorrect type }}
        @displayName={{this.displayName}}
        {{! @glint-expect-error: Incorrect type }}
        @hideShoppedHotels={{@hideHotelsMetric}}
      />
    </div>
  </template>
}
